import { useCallback, useState } from 'react';

import type { FileHeader } from '@/crypto';
import type { FileMessage } from '@/api/types';
import { saveFile } from '@/lib/download';
import { decryptFile } from '@/lib/files';

import { useToast } from './Toast';

export type FetchChunk = (messageId: string, index: number) => Promise<Uint8Array<ArrayBuffer>>;

/**
 * Downloads a file message: fetches its chunks one by one, decrypts each on
 * this device and hands the result to the browser. `progress` runs from 0 to
 * 1 while it works and is null otherwise.
 */
export function useFileDownload(message: FileMessage | null, header: FileHeader | null, bridgeKey: CryptoKey, fetchChunk: FetchChunk): { progress: number | null; download(): Promise<void> } {
  const toast = useToast();
  const [progress, setProgress] = useState<number | null>(null);

  const download = useCallback(async () => {
    if (message === null || header === null || progress !== null) return;
    setProgress(0);
    try {
      const blob = await decryptFile(bridgeKey, header, message.chunks, (index) => fetchChunk(message.id, index), setProgress);
      await saveFile(header.name || 'file', blob);
    } catch {
      toast.error('Could not download this file.');
    } finally {
      setProgress(null);
    }
  }, [message, header, progress, bridgeKey, fetchChunk, toast]);

  return { progress, download };
}

/** "45%" for a running transfer. */
export function percent(fraction: number): string {
  return `${String(Math.floor(Math.min(1, Math.max(0, fraction)) * 100))}%`;
}
